import { useRef } from 'react';
import { sendToTerminal } from '@/lib/terminalLogger';

/**
 * Hook para deduplicação de eventos de shortcuts
 * 
 * Previne execução múltipla do mesmo shortcut quando há listeners duplicados.
 * Usa timestamp + action + combo como chave única para detectar duplicatas.
 * 
 * @param windowMs - Janela de tempo para considerar eventos como duplicatas (padrão: 200ms)
 * @returns Função que retorna true se o evento é uma duplicata
 */
export function useShortcutDeduplication(windowMs: number = 200) {
  const lastShortcutRef = useRef<{
    key: string;
    timestamp: number;
  } | null>(null);

  /**
   * Verifica se um shortcut é uma duplicata
   * 
   * @param toolId - ID da ferramenta
   * @param actionId - ID da ação
   * @param combo - Combinação de teclas (opcional, para mais precisão)
   * @returns true se é duplicata, false se deve ser processado
   */
  const isDuplicate = (toolId: string, actionId: string, combo?: string): boolean => {
    const now = Date.now();
    const key = combo ? `${toolId}::${actionId}::${combo}` : `${toolId}::${actionId}`;
    
    // Verificar se é duplicata
    if (lastShortcutRef.current) {
      const timeDiff = now - lastShortcutRef.current.timestamp;
      const isSameShortcut = lastShortcutRef.current.key === key;
      
      if (isSameShortcut && timeDiff < windowMs) {
        sendToTerminal('🚫 [DEDUP] Shortcut duplicado ignorado', {
          toolId,
          actionId,
          combo,
          timeDiff: `${timeDiff}ms`,
          window: `${windowMs}ms`
        });
        return true;
      }
    }
    
    // Atualizar último shortcut processado
    lastShortcutRef.current = { key, timestamp: now };
    return false;
  };

  return { isDuplicate };
}
